import React, { useState } from 'react'
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails, Switch,
  FormControlLabel, Divider, Button, TextField, Chip
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RoundingRuleFields from './RoundingRuleFields'
import { roundComponentForMenu } from '../../utils/menuMarkup'

const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`

// Editor for per-category and per-item price overrides, shown inside the Edit
// Menu dialog under the menu-default markup. Precedence (item > category > menu)
// is applied by the backend; this UI just collects the override maps.
//
// Props:
//   categories: string[]                 category names in this menu
//   items: [{ _id, name, categoryName, pricingMode, basePrice, variantPrices }]
//   menuDefault: { pct, mode, value }    the menu-level rule (numbers/strings)
//   categoryOverrides / setCategoryOverrides   state map { [cat]: {pct,mode,value} }
//   itemOverrides / setItemOverrides           state map { [itemId]: {type, ...} }
export default function MenuOverridesEditor({
  categories, items, menuDefault,
  categoryOverrides, setCategoryOverrides,
  itemOverrides, setItemOverrides,
}) {
  const [openItemId, setOpenItemId] = useState(null)

  const menuPct = Number(menuDefault.pct) || 0
  const menuMode = menuDefault.mode || 'AS_IS'
  const menuValue = Number(menuDefault.value) || 0

  // ── Category overrides ────────────────────────────────────────────────────
  const setCat = (cat, patch) => {
    setCategoryOverrides(prev => ({ ...prev, [cat]: { ...(prev[cat] || {}), ...patch } }))
  }
  const toggleCat = (cat, on) => {
    setCategoryOverrides(prev => {
      const next = { ...prev }
      if (on) next[cat] = next[cat] || { pct: String(menuPct), mode: menuMode, value: menuValue ? String(menuValue) : '' }
      else delete next[cat]
      return next
    })
  }

  // ── Item overrides ────────────────────────────────────────────────────────
  const setItem = (id, override) => {
    setItemOverrides(prev => {
      const next = { ...prev }
      if (override == null) delete next[id]
      else next[id] = override
      return next
    })
  }

  // Effective preview for one item given its current (unsaved) override state.
  const itemFinalPreview = (item) => {
    const ov = itemOverrides[item._id]
    const base = Number(item.basePrice) || 0
    if (ov && ov.type === 'FIXED') {
      if (item.pricingMode === 'VARIANTS') {
        const vp = ov.variantPrices || {}
        const keys = Object.keys(item.variantPrices || {})
        return keys.map(k => `${k}: ${vp[k] != null && vp[k] !== '' ? fmt(vp[k]) : '—'}`).join('  ')
      }
      return ov.basePrice != null && ov.basePrice !== '' ? fmt(ov.basePrice) : '—'
    }
    // MARKUP override → its rule; else category → its rule; else menu default.
    let rule = { pct: menuPct, mode: menuMode, value: menuValue }
    if (ov && ov.type === 'MARKUP') rule = { pct: Number(ov.pct) || 0, mode: ov.mode || 'AS_IS', value: Number(ov.value) || 0 }
    else if (item.categoryName && categoryOverrides[item.categoryName]) {
      const c = categoryOverrides[item.categoryName]
      rule = { pct: Number(c.pct) || 0, mode: c.mode || 'AS_IS', value: Number(c.value) || 0 }
    }
    if (item.pricingMode === 'VARIANTS') {
      const vp = item.variantPrices || {}
      return Object.entries(vp).map(([k, v]) => `${k}: ${fmt(roundComponentForMenu(Number(v) || 0, rule.pct, rule.mode, rule.value))}`).join('  ')
    }
    return fmt(roundComponentForMenu(base, rule.pct, rule.mode, rule.value))
  }

  const itemSourceLabel = (item) => {
    const ov = itemOverrides[item._id]
    if (ov && ov.type === 'FIXED') return 'Fixed price'
    if (ov && ov.type === 'MARKUP') return 'Item markup'
    if (item.categoryName && categoryOverrides[item.categoryName]) return 'Category'
    return 'Menu default'
  }

  return (
    <Box sx={{ mt: 2 }}>
      {/* ── Category overrides ── */}
      <Accordion disableGutters sx={{ borderRadius: '10px', '&:before': { display: 'none' }, border: '1px solid #e2e8f0' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600} sx={{ color: '#1e293b' }}>
            Category overrides
            <Chip label={Object.keys(categoryOverrides).length || 'none'} size="small" sx={{ ml: 1, bgcolor: '#eef2ff', color: '#4338ca' }} />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {categories.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>Add items first — categories come from your items.</Typography>
          ) : categories.map(cat => {
            const on = !!categoryOverrides[cat]
            const o = categoryOverrides[cat] || {}
            return (
              <Box key={cat} sx={{ py: 1, borderBottom: '1px solid #f1f5f9' }}>
                <FormControlLabel
                  control={<Switch checked={on} onChange={e => toggleCat(cat, e.target.checked)} />}
                  label={<Typography fontWeight={600}>{cat}</Typography>}
                />
                {on ? (
                  <Box sx={{ pl: 1 }}>
                    <RoundingRuleFields
                      dense
                      pct={o.pct ?? ''} mode={o.mode || 'AS_IS'} value={o.value ?? ''}
                      onChange={(next) => setCat(cat, next)}
                    />
                  </Box>
                ) : (
                  <Typography variant="caption" sx={{ color: '#94a3b8', pl: 1 }}>Uses menu default.</Typography>
                )}
              </Box>
            )
          })}
        </AccordionDetails>
      </Accordion>

      {/* ── Per-item overrides ── */}
      <Accordion disableGutters sx={{ mt: 1.5, borderRadius: '10px', '&:before': { display: 'none' }, border: '1px solid #e2e8f0' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600} sx={{ color: '#1e293b' }}>
            Item overrides
            <Chip label={Object.keys(itemOverrides).length || 'none'} size="small" sx={{ ml: 1, bgcolor: '#eef2ff', color: '#4338ca' }} />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {items.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>No items in this menu yet.</Typography>
          ) : items.map(item => {
            const ov = itemOverrides[item._id]
            const kind = ov ? ov.type : 'DEFAULT'
            const isOpen = openItemId === item._id
            return (
              <Box key={item._id} sx={{ py: 1, borderBottom: '1px solid #f1f5f9' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={600} noWrap>{item.name}</Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {itemSourceLabel(item)} → <strong style={{ color: '#334155' }}>{itemFinalPreview(item)}</strong>
                    </Typography>
                  </Box>
                  <Button size="small" variant={isOpen ? 'contained' : 'outlined'} sx={{ textTransform: 'none', borderRadius: '8px' }}
                    onClick={() => setOpenItemId(isOpen ? null : item._id)}>
                    {isOpen ? 'Done' : 'Price'}
                  </Button>
                </Box>

                {isOpen && (
                  <Box sx={{ mt: 1, pl: 1 }}>
                    {/* Mode selector */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      {[['DEFAULT', 'Use default'], ['MARKUP', 'Custom markup'], ['FIXED', 'Fixed price']].map(([k, label]) => (
                        <Button key={k} size="small" variant={kind === k ? 'contained' : 'outlined'} sx={{ textTransform: 'none', borderRadius: '8px' }}
                          onClick={() => {
                            if (k === 'DEFAULT') setItem(item._id, null)
                            else if (k === 'MARKUP') setItem(item._id, { type: 'MARKUP', pct: String(menuPct), mode: menuMode, value: menuValue ? String(menuValue) : '' })
                            else setItem(item._id, { type: 'FIXED', basePrice: '', variantPrices: {} })
                          }}>
                          {label}
                        </Button>
                      ))}
                    </Box>

                    {kind === 'MARKUP' && (
                      <RoundingRuleFields
                        dense
                        pct={ov.pct ?? ''} mode={ov.mode || 'AS_IS'} value={ov.value ?? ''}
                        onChange={(next) => setItem(item._id, { type: 'MARKUP', ...next })}
                      />
                    )}

                    {kind === 'FIXED' && (
                      item.pricingMode === 'VARIANTS' ? (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {Object.keys(item.variantPrices || {}).map(size => (
                            <TextField key={size} size="small" type="number" label={size}
                              value={(ov.variantPrices || {})[size] ?? ''}
                              onChange={e => setItem(item._id, { ...ov, variantPrices: { ...(ov.variantPrices || {}), [size]: e.target.value } })}
                              InputProps={{ startAdornment: <Typography sx={{ color: '#94a3b8', mr: 0.5 }}>$</Typography> }}
                              sx={{ width: 130 }} inputProps={{ min: 0, step: 0.01 }} />
                          ))}
                        </Box>
                      ) : (
                        <TextField size="small" type="number" label="Fixed price"
                          value={ov.basePrice ?? ''}
                          onChange={e => setItem(item._id, { ...ov, basePrice: e.target.value })}
                          InputProps={{ startAdornment: <Typography sx={{ color: '#94a3b8', mr: 0.5 }}>$</Typography> }}
                          sx={{ width: 170 }} inputProps={{ min: 0, step: 0.01 }}
                          helperText="Exact final price — no markup or rounding." />
                      )
                    )}
                  </Box>
                )}
              </Box>
            )
          })}
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}
